import { bufferToHexString } from '@iotize/common/byte-converter';
import { AesEcb128Converter } from '@iotize/common/crypto';
import { deepCopy } from '@iotize/common/utility';
import {
  ServiceCallRunner,
  ServiceCallType,
  TapError,
  TapResponse,
  TapResponseStatusError,
} from '@iotize/tap';
import {
  EncryptionAlgo,
  ExecutionContext,
  RequestHandler,
  RequestInterceptor,
  ResultCode,
  TapRequestFrame,
  TapResponseFrame,
} from '@iotize/tap/client/api';
import {
  CryptedFrameConverter,
  TapClientError,
  TapRequestHelper,
  TapStreamWriter,
} from '@iotize/tap/client/impl';
import {
  EncryptedIVResponse,
  ScramService,
} from '@iotize/tap/service/impl/scram';
import { Observable, defer, throwError } from 'rxjs';
import { map, mergeMap, tap as rxTap, shareReplay } from 'rxjs/operators';

import { debug } from './debug';
import { TapScramError } from './scram-errors';

export interface EncryptionOptions {
  /**
   * true if encryption is enabled, false otherwise
   */
  encryption: boolean;

  /**
   * Initialization vector period is the number of requests sent
   * before API will change the initialization vector keys
   * The lower the period, the more secure it gets, however il will reduce communication
   * performances.
   */
  initializationVectorResetPeriod: number;

  /**
   * Encryption algo options
   */
  keys: EncryptionKeys;

  /**
   * Encrypted frame counter
   */
  frameCounter: number;
}

export interface EncryptionKeys {
  /**
   * Initialization vector used for encoding
   * If undefined, the default initialization vector will be used
   */
  ivEncode?: Uint8Array;
  /**
   * Initialization vector used for decoding
   * If undefined, the default initialization vector will be used
   */
  ivDecode?: Uint8Array;
  /**
   * Session key
   * If undefined, the session key will be regenerated when executing the first command (and if encryption is enabled)
   */
  sessionKey?: Uint8Array;
}

const INITIALIZATION_VECTOR_LENGTH = 16;
const DEFAULT_INITIALIZATION_VECTOR = '00000000000000000000000000000000';

export type InitializationVectorGeneratorFct = (length: number) => Uint8Array;

const TAG = 'ScramInterceptor';

function _hex(data: Uint8Array | undefined) {
  return data ? bufferToHexString(data) : '';
}

type ExtenedExecutionContext = ExecutionContext & { skipEncryption?: boolean };

export class ScramInterceptor implements RequestInterceptor {
  private _encryptionAlgo?: EncryptionAlgo;
  private _ivFrameCounter = 0;
  private _ivSupported = true;
  private _refreshingInitializationVectorObs?: Observable<void>;

  private get encryptionAlgo(): EncryptionAlgo {
    if (!this._encryptionAlgo) {
      throw TapClientError.illegalStateError(
        `Encryption algo has not been set yet`,
      );
    }
    return this._encryptionAlgo;
  }

  private _options: EncryptionOptions = {
    encryption: false,
    initializationVectorResetPeriod: 100,
    keys: {},
    frameCounter: 0,
  };

  private _encryptedFrameConverter = new CryptedFrameConverter({
    next: () => {
      return this._options.frameCounter++;
    },
  });

  set ivSupported(v: boolean) {
    this._ivSupported = v;
  }

  /**
   * Setter for the session key
   * @param key if null, it will stop encryption and remove session key. If true it will update session key used for encryption
   */
  public set sessionKey(key: Uint8Array | undefined) {
    this._options.keys.sessionKey = key;
    this._options.frameCounter = 0;
    if (this._options.keys.sessionKey) {
      this.refreshEncryptionAlgo();
    } else {
      this.pauseEncryption();
    }
  }

  public get sessionKey() {
    return this._options.keys.sessionKey;
  }

  /**
   * Get a copy of encryption options
   */
  public get options() {
    return deepCopy(this._options);
  }

  constructor(public scramService: ScramService) {}

  public setEncryptionKeys(options: EncryptionKeys) {
    this._options.keys = options;
    this.refreshEncryptionAlgo();
  }

  public getEncryptionKeys() {
    return this._options.keys;
  }

  public setFrameCounter(value: number) {
    this._options.frameCounter = value;
  }

  setInitializationVectorRefreshPeriod(period: number) {
    this._options.initializationVectorResetPeriod = period;
  }

  intercept(context: ExtenedExecutionContext, next: RequestHandler) {
    const request = context.request;
    debug(
      TAG,
      `exec ${TapRequestHelper.toString(request)} (encryption:${
        this._options.encryption
      }, frameCounter: ${this._options.frameCounter} - skip ${
        context.skipEncryption
      }, key: ${_hex(this.sessionKey)} iv decode: ${_hex(
        this._options.keys.ivDecode,
      )}, iv encode: ${_hex(this._options.keys.ivEncode)})`,
    );
    let obs: Observable<TapResponseFrame>;
    if (this._options.encryption && !context.skipEncryption) {
      obs = this._sendWithEncryption(context, next).pipe(
        map<TapResponse<any>, TapResponseFrame>((response) => {
          const data = response.rawBody();
          return {
            status: response.status,
            data,
          };
        }),
      );
    } else {
      obs = next.handle(context);
    }
    return obs.pipe(
      rxTap((response) => {
        debug(
          TAG,
          `Response to ${TapRequestHelper.toString(request)} => ${
            response.status
          } ${bufferToHexString(response.data)}`,
        );
      }),
    );
  }

  private _sendWithEncryption(
    context: ExtenedExecutionContext,
    next: RequestHandler,
  ): Observable<TapResponse<any>> {
    if (!this._encryptedFrameConverter) {
      return throwError(
        TapClientError.illegalStateError(
          `Encrypted frame converter has not been specified yet`,
        ),
      );
    }
    let callObs: Observable<TapResponse<any>>;
    let newClientIV: Uint8Array | undefined;

    if (this._options.initializationVectorResetPeriod === 1) {
      newClientIV = this.initializationVectorGenerator(
        INITIALIZATION_VECTOR_LENGTH,
      );
      this._setEncodeIV(newClientIV);
      debug(
        TAG,
        `Changing initialization vector for every requests. New client IV: ${_hex(
          newClientIV,
        )}`,
      );
      const cryptedFrame = this._buildEncryptedFrame(context);
      const call = this.scramService.sendWithIVCall({
        request: cryptedFrame,
        iv: newClientIV,
      });
      callObs = this._toCallObservable<any, EncryptedIVResponse>(
        {
          ...context,
          skipEncryption: true,
        },
        next,
        call,
      ).pipe(
        map((response: TapResponse<EncryptedIVResponse>) => {
          // if (response.isSuccessful() && response.rawBody().length < INITIALIZATION_VECTOR_LENGTH){
          //     // tap firmware version is too old apparentl.
          //     // CCOM IV resource was already used to generate rand number before firmware version 1.83, thats why there is a success code result
          //     throw TapError.initializationVectorNotSupported(
          //         new Error(`Tap response is too short`)
          //     );
          // }

          const body: EncryptedIVResponse = response.body();
          debug(
            TAG,
            `Refreshing decoding initialization vector: ${_hex(body.iv)}`,
          );
          this._setDecodeIV(body.iv);
          return TapResponse.create(response.status, body.response);
        }),
      );
    } else {
      if (
        this._options.initializationVectorResetPeriod > 1 &&
        this._ivFrameCounter >= this._options.initializationVectorResetPeriod &&
        !isRefreshInitializationVectorRequest(context.request)
      ) {
        debug(
          TAG,
          `_ivInterceptor refreshEncryptionInitializationVector is required ${this._ivFrameCounter}/${this._options.initializationVectorResetPeriod}`,
        );
        if (!this._refreshingInitializationVectorObs) {
          this._refreshingInitializationVectorObs = defer(async () => {
            try {
              return await this.refreshInitializationVectors();
            } finally {
              this._refreshingInitializationVectorObs = undefined;
            }
          }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
        } else {
          // debug(TAG, `Refreshing initialization vector has already been asked`);
        }
        callObs = this._refreshingInitializationVectorObs.pipe(
          mergeMap((_) => {
            const encryptedFrame = this._buildEncryptedFrame(context);
            return this._toCallObservable(
              context,
              next,
              this.scramService.sendCall(encryptedFrame),
            );
          }),
        ) as Observable<TapResponse<any>>;
      } else {
        const encryptedFrame = this._buildEncryptedFrame(context);
        const call = this.scramService.sendCall(encryptedFrame);
        callObs = this._toCallObservable(context, next, call);
      }
    }
    return callObs.pipe(
      map<TapResponse<Uint8Array>, TapResponse<any>>(
        (encryptedWarpperResponse: TapResponse<Uint8Array>) => {
          this._ivFrameCounter++;
          // debug(TAG, `Iotize client crypted frame: ${encryptedWarpperResponse}`);
          if (!encryptedWarpperResponse.isSuccessful()) {
            if (
              encryptedWarpperResponse.codeRet() ===
              ResultCode.SERVICE_UNAVAILABLE
            ) {
              throw TapScramError.scramNotStartedYet(context.request);
            } else if (
              encryptedWarpperResponse.codeRet() === ResultCode.BAD_REQUEST
            ) {
              throw TapScramError.invalidScramKey(context.request);
            }
            encryptedWarpperResponse.successful();
          }
          const cryptedFrameContent = this.encryptionAlgo.decode(
            encryptedWarpperResponse.rawBody(),
          );
          // debug(TAG, `Decrypted frame value: ${_hex(cryptedFrameContent)}`);
          const apduFrame =
            this._encryptedFrameConverter.decode(cryptedFrameContent);
          // debug(TAG, `apduFrame frame value: ${_hex(apduFrame)}`);
          const tapResponse = context.client.responseDecoder.decode(apduFrame);

          const apiResponse = new TapResponse(tapResponse, context.request);

          // let response: TapResponse<any> = this.lwm2mResponseConverter.decode(lwm2mResponseFrame.data);
          if (context.bodyDecoder) {
            apiResponse.setBodyDecoder(context.bodyDecoder);
          }
          // debug(TAG, `Iotize client decoded response: ${response}`)
          return apiResponse;
        },
      ),
    );
  }

  private _buildEncryptedFrame(context: ExtenedExecutionContext): Uint8Array {
    const iotizeFrame = TapStreamWriter.create(
      10 + context.request.payload.length,
    ).writeTapRequestFrame(context.request).toBytes;
    const encryptedFrameModel =
      this._encryptedFrameConverter.encode(iotizeFrame);
    const encryptedFrame = this.encryptionAlgo.encode(encryptedFrameModel);
    return encryptedFrame;
  }

  private _setEncodeIV(iv: Uint8Array) {
    this._options.keys.ivEncode = iv;
    this.refreshEncryptionAlgo();
  }

  private _setDecodeIV(iv: Uint8Array) {
    this._options.keys.ivDecode = iv;
    this.refreshEncryptionAlgo();
  }

  private _toCallObservable<BodyType, ReturnType>(
    context: ExtenedExecutionContext,
    next: RequestHandler,
    call: ServiceCallType<BodyType, ReturnType>,
  ) {
    const tapRequest = ServiceCallRunner.toTapRequest(call);
    const bodyDecoderFct = ServiceCallRunner.resolveResponseBodyDecoder(call);
    const bodyDecoder = bodyDecoderFct
      ? {
          decode: bodyDecoderFct,
        }
      : undefined;
    return next
      .handle({
        ...context,
        request: tapRequest,
        bodyDecoder,
      })
      .pipe(
        map((tapResponse) => {
          return new TapResponse(tapResponse, context.request, bodyDecoder);
        }),
      );
  }

  /**
   * Refresh initialization vectors
   */
  public async refreshInitializationVectors(): Promise<void> {
    const clientIV = this.initializationVectorGenerator(
      INITIALIZATION_VECTOR_LENGTH,
    );
    try {
      debug(TAG, `Performing initialization vector refresh`);
      const serverIV = (
        await this.scramService.setInitializationVector(clientIV)
      ).body();
      this._options.keys.ivEncode = clientIV;
      this._options.keys.ivDecode = serverIV;
      this._ivFrameCounter = 0;
      debug(
        TAG,
        `Initialization vector refreshed: server ${_hex(
          serverIV,
        )} client=${_hex(clientIV)})`,
      );
      this.refreshEncryptionAlgo();
    } catch (err) {
      debug(
        TAG,
        `Cannot refresh initialization vector: ${(err as Error).message}`,
      );
      if (err instanceof TapResponseStatusError) {
        const tapResponseStatus = err.response.status;
        if (
          tapResponseStatus === ResultCode.NOT_IMPLEMENTED ||
          tapResponseStatus === ResultCode.NOT_FOUND
        ) {
          debug(
            TAG,
            `Cannot initialize encryption vectors (firmware version does not support it). Error: ${err.message}`,
          );
          this._ivSupported = false;
          throw TapError.initializationVectorNotSupported(err);
        }
      }
      throw err;
    }
  }

  public initializationVectorGenerator: InitializationVectorGeneratorFct = (
    length: number,
  ): Uint8Array => {
    const data = new Array(length).fill(0).map((_) => {
      return Math.floor(Math.random() * 0xff);
    });
    return Uint8Array.from(data);
  };

  /**
   * Resume encryption session
   */
  public resumeEncryption() {
    debug(TAG, `resume encryption`);
    this._options.encryption = true;
  }

  /**
   * Pause encryption without destorying session (session keys will not be removed)
   */
  public pauseEncryption() {
    debug(TAG, `pause encryption`);
    this._options.encryption = false;
  }

  /**
   * Initialize a new encrypted sesssion
   * Session key will be changed. Initialization vector too (if firmware supports it)
   */
  public async newSession() {
    try {
      debug(TAG, `Creating new encryption session`);
      this.clearSession();
      const response = await this.scramService.initialize();
      response.successful();
      const newSessionKey = response.body();
      this.sessionKey = newSessionKey;
      debug(
        TAG,
        `Session key will be ${_hex(newSessionKey)} (length=${
          newSessionKey.length
        })`,
      );
      if (this._options.initializationVectorResetPeriod > 0) {
        try {
          await this.refreshInitializationVectors();
        } catch (err) {
          // Ignore initialization vector not supported error
          if (
            (err as TapError).code !==
            TapError.Code.InitializationVectorNotSupported
          ) {
            throw err;
          }
        }
      }
      return newSessionKey;
    } catch (err) {
      throw TapError.cannotStartScram(err as Error);
    }
  }

  /**
   * Clear encrypted session.
   * Destroys frame counter, session keys and initialization vectors
   * This will also stop encrypted communication if it was running
   */
  public clearSession() {
    this.pauseEncryption();
    this._options.keys.ivDecode = undefined;
    this._options.keys.ivEncode = undefined;
    this._options.keys.sessionKey = undefined;
    this._ivFrameCounter = 0;
  }

  private refreshEncryptionAlgo() {
    if (this._options.keys.sessionKey) {
      const algo = new AesEcb128Converter({
        key: _hex(this._options.keys.sessionKey),
        ivDecode: this._options.keys.ivDecode
          ? _hex(this._options.keys.ivDecode)
          : DEFAULT_INITIALIZATION_VECTOR,
        ivEncode: this._options.keys.ivEncode
          ? _hex(this._options.keys.ivEncode)
          : DEFAULT_INITIALIZATION_VECTOR,
      });
      this._encryptionAlgo = algo;
    }
  }

  // /**
  //  * Enable/Disable encrytion when communicating with a device
  //  * @param enable true if requests must be encrypted
  //  * @param resetSessionKey true if you want to reset the the session key.
  //  * @param refreshInitializationVector true if you want to change initialization vectors
  //  *
  //  */
  // public async encryption(
  //     enable: boolean,
  //     resetSessionKey = false,
  //     refreshInitializationVector = false
  // ): Promise<void> {
  //     // If encryption was not enabled, tap will reset session key when enabling encryption
  //     if (enable) {
  //         if (resetSessionKey || !this._options.keys.sessionKey) {
  //             await this.newSession();
  //         }
  //     } else {
  //         if (resetSessionKey) {
  //             this._options.keys.sessionKey = undefined;
  //         }
  //         if (refreshInitializationVector) {
  //             this._options.keys.ivDecode = undefined;
  //             this._options.keys.ivEncode = undefined;
  //             this._ivFrameCounter = 0;
  //         }
  //     }
  //     if (enable) {
  //         this.enableEncryption();
  //     } else {
  //         this.disableEncryption();
  //     }

  //     if (
  //         refreshInitializationVector &&
  //         ((this._ivSupported && !this._options.keys.ivEncode) ||
  //             !this._options.keys.ivDecode)
  //     ) {
  //         try {
  //             await this.refreshEncryptionInitializationVector();
  //         } catch (err) {
  //             // Ignore initialization vector not supported error
  //             if (
  //                 err.code !== TapError.Code.InitializationVectorNotSupported
  //             ) {
  //                 throw err;
  //             }
  //         }
  //     }
  // }
}

const SCRAM_CALL_FACTORY = new ScramService(undefined as any);

function isRefreshInitializationVectorRequest(request: TapRequestFrame) {
  return (
    TapRequestHelper.pathToString(request.header.path) ===
    SCRAM_CALL_FACTORY.resources.setInitializationVector.path
  );
}
